// The one trigger for both AI passes (idea->capability mapping and assumption
// verdicts). Sits between question 1 (deterministic, needs no run) and the two
// questions the run feeds. The re-ground section above keeps its own button:
// it moves edges, a different job.

import { Scale, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatRelativeTime } from '@/lib/utils';
import type { AlignmentProposalRow } from '@/types/api';

export function ReviewBar({
  row,
  isLoading,
  isError,
  canEdit,
  generating,
  onGenerate,
}: {
  row: AlignmentProposalRow | null | undefined;
  // The first read of the proposal row. Until it settles we do not know
  // whether a run with pending decisions exists, so the button waits.
  isLoading: boolean;
  isError: boolean;
  canEdit: boolean;
  generating: boolean;
  onGenerate: () => void;
}) {
  // The model was asked about nothing: every capability is already mapped and
  // every untested assumption already carries a call.
  const nothingToWeigh = !!row && row.capabilityCountAtRun === 0 && row.assumptionCountAtRun === 0;

  return (
    <section className="rounded-lg border bg-card p-5 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0 flex-1 space-y-1">
        <p className="font-semibold flex items-center gap-2">
          <Scale className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Let the AI answer questions 2 and 3.
        </p>
        <p className="text-sm text-muted-foreground">
          It reads your idea, your capabilities, your market findings and what customers said, then suggests
          answers below: which capabilities were part of your original idea, and how each untested assumption
          stands against the evidence. You confirm or change every call.
        </p>
        {row && (
          <p className="text-xs text-muted-foreground">
            Last checked {formatRelativeTime(row.generatedAt)}
            {nothingToWeigh ? '. Nothing new to weigh. Every capability is sorted and every untested assumption has a call.' : '.'}
          </p>
        )}
        {isError && (
          <p className="flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            Couldn't load suggestions. Weigh again to retry.
          </p>
        )}
      </div>
      {canEdit && (
        <Button size="sm" variant={row ? 'outline' : 'default'} disabled={generating || isLoading} onClick={onGenerate}>
          {generating ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" aria-hidden="true" /> Weighing…
            </>
          ) : row ? (
            'Weigh again'
          ) : (
            'Weigh the evidence'
          )}
        </Button>
      )}
    </section>
  );
}
